import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Bookmark, Search, Tag, X, Eye, EyeOff, Info } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { 
  getSavedRssItems, 
  markRssItemRead, 
  saveRssItem,
  RssFeedItem
} from '../lib/rss.service';

const SavedRSSItems: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const queryClient = useQueryClient();

  // Fetch saved RSS items
  const { 
    data: savedItems = [], 
    isLoading, 
    isError, 
    error 
  } = useQuery({
    queryKey: ['savedRssItems'],
    queryFn: getSavedRssItems,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Mark item as read mutation
  const markReadMutation = useMutation({
    mutationFn: ({ itemId, isRead }: { itemId: string; isRead: boolean }) => 
      markRssItemRead(itemId, isRead),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savedRssItems'] });
    },
    onError: (error) => {
      toast.error(`Failed to update read status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  // Unsave item mutation
  const unsaveItemMutation = useMutation({
    mutationFn: (itemId: string) => saveRssItem(itemId, false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savedRssItems'] });
      toast.success('Item removed from saved items');
    },
    onError: (error) => {
      toast.error(`Failed to remove item: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  // Filter items based on search
  const filteredItems = savedItems.filter(item => 
    searchTerm === '' || 
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle item click
  const handleItemClick = (item: RssFeedItem) => {
    window.open(item.link, '_blank');
    
    // Mark as read if not already
    if (!item.is_read) {
      markReadMutation.mutate({ itemId: item.id, isRead: true });
    }
  };

  // Handle unsave
  const handleUnsave = (e: React.MouseEvent, item: RssFeedItem) => {
    e.stopPropagation();
    unsaveItemMutation.mutate(item.id);
  };

  // Handle read/unread
  const handleReadToggle = (e: React.MouseEvent, item: RssFeedItem) => {
    e.stopPropagation();
    markReadMutation.mutate({ itemId: item.id, isRead: !item.is_read });
  };

  if (isLoading) {
    return (
      <div className="animate-pulse bg-white rounded-lg shadow-sm p-6 space-y-4">
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center text-gray-500">
          <p>{error instanceof Error ? error.message : 'Failed to load saved items'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Saved Articles</h2>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search saved articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full border rounded-lg py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>
        
        {/* Saved Items */}
        <div className="space-y-6">
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <div
                key={item.id}
                className={`border-b border-gray-100 pb-6 last:border-b-0 last:pb-0 ${
                  item.is_read ? 'opacity-75' : ''
                } cursor-pointer hover:bg-gray-50 -mx-6 px-6`}
                onClick={() => handleItemClick(item)}
              >
                <div className="flex justify-between items-start">
                  <h3 className="text-lg font-medium text-gray-900 mb-2 flex items-start pr-16">
                    {item.title}
                  </h3>
                  <div className="flex space-x-2 shrink-0">
                    <button
                      onClick={(e) => handleReadToggle(e, item)}
                      className="p-1 rounded-full hover:bg-gray-100"
                      title={item.is_read ? "Mark as unread" : "Mark as read"}
                    >
                      {item.is_read ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-500" />
                      )}
                    </button>
                    <button
                      onClick={(e) => handleUnsave(e, item)}
                      className="p-1 rounded-full hover:bg-gray-100"
                      title="Remove from saved"
                    >
                      <Bookmark className="h-5 w-5 text-primary" />
                    </button>
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="p-1 rounded-full hover:bg-gray-100"
                      title="Open in new tab"
                    >
                      <ExternalLink className="h-5 w-5 text-gray-500" />
                    </a>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                  {item.description}
                </p>
                <div className="flex items-center text-sm text-gray-500">
                  <span className="font-medium text-gray-700">{item.source?.name || 'Unknown Source'}</span>
                  <span className="mx-2">•</span>
                  <span>{format(new Date(item.published_at), 'MMM d, yyyy')}</span>
                  {item.source?.category && (
                    <>
                      <span className="mx-2">•</span>
                      <span>{item.source.category}</span>
                    </>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Info className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-2">No saved articles found</p>
              <p className="text-sm text-gray-400">
                Save articles by clicking the bookmark icon
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavedRSSItems;